import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";

const MayaLeadsInbox = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);

  const loadLeads = async () => {
    setLoading(true);
    try {
      const res = await api.get("/integrations/maya/leads");
      setLeads(res.data || []);
    } catch (e) {
      toast.error("Failed to load Maya leads.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLeads();
  }, []);

  const markContacted = async (lead) => {
    setActingId(lead.id);
    try {
      await api.post("/integrations/maya/leads/" + lead.id + "/contacted");
      toast.success("Lead marked as contacted.");
      loadLeads();
    } catch (e) {
      toast.error("Failed to update lead.");
    } finally {
      setActingId(null);
    }
  };

  const convertLead = async (lead) => {
    setActingId(lead.id);
    try {
      await api.post("/integrations/maya/leads/" + lead.id + "/convert");
      toast.success("Lead converted to client.");
      loadLeads();
    } catch (e) {
      toast.error("Failed to convert lead.");
    } finally {
      setActingId(null);
    }
  };

  const statusVariant = (status) => {
    if (status === "contacted") {
      return "secondary";
    }
    if (status === "converted") {
      return "outline";
    }
    return "default";
  };

  const formatTime = (value) => {
    if (!value) {
      return "-";
    }
    const d = new Date(value);
    if (isNaN(d.getTime())) {
      return value;
    }
    return d.toLocaleString();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Maya Leads</h1>
          <p className="text-sm text-slate-500">Callers Maya could not match to an existing client. Matched calls go straight to the client activity timeline.</p>
        </div>
        <Button type="button" variant="outline" onClick={loadLeads} disabled={loading}>Refresh</Button>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Inbox ({leads.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading && (<p className="text-sm text-slate-500">Loading Maya leads...</p>)}
          {!loading && leads.length === 0 && (<p data-testid="maya-leads-empty" className="text-sm text-slate-500">No Maya leads yet. Events from unknown callers will appear here.</p>)}
          {!loading && leads.length > 0 && (
            <div data-testid="maya-leads-list" className="space-y-3">
              {leads.map((lead) => (
                <div key={lead.id} className="rounded-md border border-slate-200 p-4 space-y-2">
                  <div className="flex items-center justify-between gap-4">
                    <p className="font-medium text-slate-900">{lead.name || "Unknown caller"}</p>
                    <Badge variant={statusVariant(lead.status)}>{lead.status || "new"}</Badge>
                  </div>
                  <p className="text-sm text-slate-600">{lead.summary || "No summary."}</p>
                  <p className="text-sm text-slate-500">{lead.phone || "-"}{lead.email ? " | " + lead.email : ""}</p>
                  <p className="text-sm text-slate-500">Received: {formatTime(lead.received_at)}</p>
                  <div className="flex flex-wrap gap-2">
                    {lead.status === "new" && (
                      <Button type="button" variant="outline" size="sm" onClick={() => markContacted(lead)} disabled={actingId === lead.id}>Mark contacted</Button>
                    )}
                    {lead.status !== "converted" && (
                      <Button type="button" size="sm" onClick={() => convertLead(lead)} disabled={actingId === lead.id}>Convert to client</Button>
                    )}
                    {lead.status === "converted" && lead.converted_client_id && (
                      <Link className="text-sm font-medium text-teal-700 hover:underline" to={"/crm/clients/" + lead.converted_client_id}>View client</Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MayaLeadsInbox;
